'use client';

import { Fragment } from 'react';
import styles from './TransparencyBoard.module.scss';
import { gaClick } from '@/src/helpers/gtag';
import { useAOS } from '@/src/lib/hooks/useAOS';

interface ToolMetric {
  name: string;
  value: string;
  label: string;
  status: 'good' | 'warning' | 'neutral';
}

interface TransparencyBoardProps {
  eyebrow?: string;
  title?: string;
  highlightedTitle?: string;
  description?: string;
  progressLabel?: string;
  progressValue?: number;
  tools?: ToolMetric[];
  backgroundImage?: string;
  tilt?: 'left' | 'right';
  illustrativeLabel?: string;
  lang?: string;
}

const DEFAULT_ILLUSTRATIVE_LABEL = (lang: string) =>
  lang === 'en'
    ? 'Example project dashboard — illustrative data.'
    : 'Exemple de dashboard projet — données illustratives.';

const DEFAULT_TOOLS: ToolMetric[] = [
  { name: 'Lighthouse', value: '98', label: '/100', status: 'good' },
  { name: 'Cypress', value: '142', label: 'tests', status: 'good' },
  { name: 'GitHub Actions', value: 'OK', label: 'CI/CD', status: 'good' },
  { name: 'Snyk', value: '0', label: 'vuln.', status: 'good' },
  { name: 'Datadog', value: 'Live', label: 'monitoring', status: 'neutral' },
  { name: 'Jest', value: '94', label: '% cov.', status: 'good' },
];

const renderTitle = (text: string) => {
  const parts = text.split('*');
  return parts.map((part, i) =>
    i % 2 === 1 ? <em key={i}>{part}</em> : <Fragment key={i}>{part}</Fragment>
  );
};

export function TransparencyBoard({
  eyebrow = 'TRANSPARENCE TOTALE',
  title = 'Suivez la qualité et l’avancée',
  highlightedTitle = '*en temps réel*.',
  description = 'Des outils indépendants et objectifs partagent leurs métriques avec vous. Vous gardez le contrôle sans la charge opérationnelle.',
  progressLabel = 'Avancement global',
  progressValue = 72,
  tools = DEFAULT_TOOLS,
  backgroundImage,
  tilt = 'right',
  illustrativeLabel,
  lang = 'fr',
}: TransparencyBoardProps) {
  useAOS();

  const resolvedIllustrativeLabel = illustrativeLabel ?? DEFAULT_ILLUSTRATIVE_LABEL(lang);

  const tiltClass = tilt === 'left' ? styles.tiltLeft : styles.tiltRight;
  const hasBgClass = backgroundImage ? styles.hasBg : '';

  return (
    <div
      className={`${styles.root} ${tiltClass} ${hasBgClass}`}
      id="transparence"
      style={backgroundImage ? { backgroundImage: `url(${backgroundImage})` } : undefined}
    >
      <div className={styles.bg} aria-hidden="true" />
      <div className={styles.inner}>
        <div className={styles.text} data-aos="fade-up" data-aos-duration="700">
          {eyebrow && <p className={styles.eyebrow}>{eyebrow}</p>}
          <h2 className={styles.title}>
            {renderTitle(title)} {renderTitle(highlightedTitle)}
          </h2>
          <p className={styles.description}>{description}</p>
        </div>

        <div
          className={styles.board}
          data-aos="fade-up"
          data-aos-duration="900"
          data-aos-delay="150"
        >
          <div className={styles.boardHeader}>
            <div className={styles.liveBadge}>
              <span className={styles.pulse} />
              <span>Live</span>
            </div>
            <span className={styles.boardTitle}>Tableau de bord projet</span>
          </div>

          <div className={styles.grid}>
            {tools.map((tool, i) => (
              <div
                key={tool.name}
                className={styles.toolCard}
                data-aos="fade-up"
                data-aos-duration="600"
                data-aos-delay={200 + i * 60}
                role="button"
                tabIndex={0}
                onClick={() => gaClick('transparency_board_tool_card', { tool_name: tool.name })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    gaClick('transparency_board_tool_card', { tool_name: tool.name });
                  }
                }}
              >
                <span className={`${styles.status} ${styles[tool.status]}`} />
                <span className={styles.toolName}>{tool.name}</span>
                <div className={styles.toolValue}>
                  <strong>{tool.value}</strong>
                  <small>{tool.label}</small>
                </div>
              </div>
            ))}
          </div>

          <div className={styles.progress}>
            <div className={styles.progressHeader}>
              <span>{progressLabel}</span>
              <span>{progressValue}%</span>
            </div>
            <div className={styles.progressTrack}>
              <div className={styles.progressFill} style={{ width: `${progressValue}%` }} />
            </div>
          </div>

          {resolvedIllustrativeLabel && (
            <p className={styles.illustrativeCaption}>{resolvedIllustrativeLabel}</p>
          )}
        </div>
      </div>
    </div>
  );
}
